import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { BadgeArt, svgToDataUrl, tierStyle, useBadgeLib } from "../art";
import { TierLineView } from "../TierName";
import { STORY_LAYOUT as L, STORY_W, hexToRgba, patternTileSvg, storyText, storyTheme, type StoryInput } from "./storyTheme";
import "./badgeShare.css";

/**
 * The 9:16 story as a page element: the same layout as the saved PNG
 * (storyImage.ts), with the badge alive (idle motion). Scales to its width.
 */
const BadgeStoryFrame: React.FC<{ input: StoryInput; className?: string }> = ({ input, className }) => {
  const { lib } = useBadgeLib();
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(360);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const w = el.getBoundingClientRect().width;
      if (w > 0) setWidth(w);
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const u = width / STORY_W;
  const { badge } = input;
  const tier = String(badge.tier);
  const theme = storyTheme(tier);
  const text = storyText(input, lib);
  const pattern = useMemo(
    () => svgToDataUrl(patternTileSvg(lib, badge.category, tier, theme.pattern, theme.patternAlpha)),
    [lib, badge.category, tier, theme.pattern, theme.patternAlpha]
  );

  const vars = {
    "--u": `${u}px`,
    "--bs-top": theme.top,
    "--bs-bottom": theme.bottom,
    "--bs-ink": theme.ink,
    "--bs-light": hexToRgba(theme.light, theme.lightAlpha),
    "--bs-light-mid": hexToRgba(theme.light, theme.lightAlpha * 0.45),
    "--bs-light-0": hexToRgba(theme.light, 0),
    "--bs-rays": hexToRgba(theme.rays, theme.raysAlpha),
    "--bs-tier": tierStyle(tier).fill,
  } as React.CSSProperties;

  return (
    <div ref={ref} className={`bstory${className ? ` ${className}` : ""}`} style={vars} data-tier={tier}>
      <div className="bstory__pattern" style={{ backgroundImage: `url("${pattern}")` }} aria-hidden="true" />
      <div className="bstory__rays" aria-hidden="true" />
      <div className="bstory__light" aria-hidden="true" />

      <div className="bstory__top">
        <span className="bstory__wordmark">{text.wordmark}</span>
        <span className="bstory__kicker">{text.kicker}</span>
      </div>

      <div className="bstory__badge">
        <BadgeArt
          category={badge.category}
          tier={badge.tier}
          number={badge.number}
          count={input.count}
          code={badge.code}
          name={badge.name}
          size={Math.max(1, Math.round(L.badge.size * u))}
          variant="full"
          animate="idle"
        />
      </div>

      {text.stamp && (
        <div className="bstory__stamp" data-testid="badge-first-stamp">
          {text.stamp}
        </div>
      )}

      <div className="bstory__card">
        <h1 className="bstory__name" style={{ fontSize: `calc(${text.nameSize} * var(--u))` }}>
          {text.name}
        </h1>
        <p className="bstory__line">
          <TierLineView line={{ lead: null, tier: text.tier, label: text.tierLabel, fact: text.fact }} />
        </p>
        <div className="bstory__rule" />
        <div className="bstory__by">{text.by}</div>
        <div className="bstory__foot">
          <span className="bstory__user">{text.username}</span>
          <span className="bstory__date">{text.date}</span>
        </div>
      </div>
    </div>
  );
};

export default BadgeStoryFrame;
